import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Sparkles, LayoutGrid, Tag } from "lucide-react";

import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import type { CurrentUser } from "../../types/user";

interface WelcomeHeroProps {
  currentUser?: CurrentUser | null;
  postCount: number;
  categoryCount: number;
  contentTypeCount: number;
  /** Controls the login-triggered slide-in/auto-hide behavior. */
  visible?: boolean;
}

function displayName(user?: CurrentUser | null) {
  return user?.firstName || user?.name || user?.username || "there";
}

function initials(user?: CurrentUser | null) {
  const source = user?.firstName || user?.name || user?.username || "";
  return source.slice(0, 2).toUpperCase() || "?";
}

export function WelcomeHero({
  currentUser,
  postCount,
  categoryCount,
  contentTypeCount,
  visible = true,
}: WelcomeHeroProps) {
  // Signed-out visitors never see the welcome banner.
  const isShown = Boolean(currentUser) && visible;

  return (
    <AnimatePresence>
      {isShown && (
        <motion.section
          key="welcome-hero"
          initial={{ opacity: 0, x: -120 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 120, height: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-gradient-to-br from-[#fc991d]/30 via-orange-300/20 to-transparent blur-3xl" />
            <div className="absolute -bottom-24 -left-10 h-56 w-56 rounded-full bg-gradient-to-tr from-slate-700/20 via-sky-400/10 to-transparent blur-3xl dark:from-slate-500/20" />
          </div>

          <div className="relative flex flex-col items-center gap-4 p-5 text-center sm:flex-row sm:items-center sm:text-left sm:p-8">
            <div className="relative shrink-0">
              <Avatar className="h-16 w-16 sm:h-20 sm:w-20 ring-4 ring-white dark:ring-slate-800 shadow-md">
                {currentUser?.picture ? (
                  <AvatarImage
                    src={currentUser.picture}
                    alt={displayName(currentUser)}
                    className="object-cover"
                  />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-900 text-white text-lg font-semibold">
                  {initials(currentUser)}
                </AvatarFallback>
              </Avatar>
              <span
                className="absolute bottom-0.5 right-0.5 h-4 w-4 rounded-full bg-gradient-to-br from-[#fc991d] to-orange-500 ring-2 ring-white dark:ring-slate-900"
                aria-label="Signed in"
              />
            </div>

            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                {`Welcome back, ${displayName(currentUser)}!`}
              </h1>
              <p className="mt-1.5 text-sm sm:text-base text-slate-500 dark:text-slate-400 max-w-md">
                Here's what the community has been sharing — jump back in.
              </p>
            </div>
          </div>

          <div className="relative grid grid-cols-2 gap-px border-t border-slate-200 dark:border-slate-700 bg-slate-200/60 dark:bg-slate-700/60 sm:grid-cols-3">
            <StatChip icon={<LayoutGrid className="h-4 w-4" />} label="Posts in view" value={postCount} />
            <StatChip icon={<Sparkles className="h-4 w-4" />} label="Categories" value={categoryCount} />
            <StatChip
              icon={<Tag className="h-4 w-4" />}
              label="Content types"
              value={contentTypeCount}
              className="col-span-2 sm:col-span-1"
            />
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
}

function StatChip({
  icon,
  label,
  value,
  className = "",
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-3 bg-white dark:bg-slate-900 px-4 py-3 sm:px-6 sm:py-4 ${className}`}>
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-none">{value}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{label}</p>
      </div>
    </div>
  );
}
